import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PageShell, PageHero } from "@/components/PageShell";
import { CheckCircle2, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/consent/$token")({
  component: ConsentPage,
  head: () => ({
    meta: [
      { title: "Sign consent form, N S Physiotherapy" },
      { name: "description", content: "HPCSA aligned physiotherapy consent form for your upcoming appointment." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Acks = {
  interviewAndAssessment: boolean;
  exposeAffectedArea: boolean;
  possibleDiscomfort: boolean;
  rightToStop: boolean;
  consentToTreatment: boolean;
  medicalAidClaim: boolean;
  questionsAnswered: boolean;
};

function ConsentPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [acks, setAcks] = useState<Acks>({
    interviewAndAssessment: false,
    exposeAffectedArea: false,
    possibleDiscomfort: false,
    rightToStop: false,
    consentToTreatment: false,
    medicalAidClaim: false,
    questionsAnswered: false,
  });
  const [signedName, setSignedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiredKeys: (keyof Acks)[] = [
    "interviewAndAssessment",
    "exposeAffectedArea",
    "possibleDiscomfort",
    "rightToStop",
    "consentToTreatment",
    "questionsAnswered",
  ];
  const allRequired = requiredKeys.every((k) => acks[k]);
  const canSubmit = allRequired && signedName.trim().length >= 2 && !submitting;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/public/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          signed_name: signedName.trim(),
          acknowledgements: {
            interviewAndAssessment: true,
            exposeAffectedArea: true,
            possibleDiscomfort: true,
            rightToStop: true,
            consentToTreatment: true,
            medicalAidClaim: acks.medicalAidClaim,
            questionsAnswered: true,
          },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error === "invalid_token" ? "This consent link is invalid or has expired." : "Something went wrong. Please try again.");
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Consent form"
        title="Physiotherapy consent"
        subtitle="Please read carefully and sign below. This form is aligned with HPCSA guidance."
      />
      <section className="container-page py-10">
        {done ? (
          <div className="max-w-2xl mx-auto rounded-3xl border border-border bg-card p-8 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
            <h2 className="mt-4 font-display text-2xl font-bold text-primary">Consent submitted</h2>
            <p className="mt-2 text-foreground/75">Thank you. We've emailed you a copy for your records. We look forward to seeing you at your appointment.</p>
            <button onClick={() => navigate({ to: "/" })} className="mt-6 btn-hero inline-flex h-11 items-center rounded-full px-5 font-semibold">Back to home</button>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-2xl mx-auto grid gap-5">
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                <div className="text-sm text-foreground/85">
                  <div className="font-semibold text-primary">What to expect</div>
                  <p className="mt-1">By signing this form you confirm you understand what your physiotherapy session will involve, and you agree to the treatment described below. You can ask questions at any time and stop the session whenever you need to.</p>
                </div>
              </div>
            </div>

            <fieldset className="grid gap-3 rounded-2xl border border-border bg-secondary/40 p-5 text-sm">
              <legend className="px-2 font-semibold text-primary">Please acknowledge each item</legend>

              <Check label="I understand that the session will include an interview and physical assessment." checked={acks.interviewAndAssessment} onChange={(v) => setAcks({ ...acks, interviewAndAssessment: v })} />
              <Check label="I understand that I may be asked to remove some clothing to expose the affected area so it can be properly assessed and treated. Draping and privacy will be respected at all times." checked={acks.exposeAffectedArea} onChange={(v) => setAcks({ ...acks, exposeAffectedArea: v })} />
              <Check label="I understand that some techniques may cause discomfort during or after the session, and that risks and benefits have been explained to my satisfaction." checked={acks.possibleDiscomfort} onChange={(v) => setAcks({ ...acks, possibleDiscomfort: v })} />
              <Check label="I understand that I may withdraw consent and stop the session at any time, without penalty." checked={acks.rightToStop} onChange={(v) => setAcks({ ...acks, rightToStop: v })} />
              <Check label="I consent to physiotherapy assessment and treatment for the condition I described in my booking." checked={acks.consentToTreatment} onChange={(v) => setAcks({ ...acks, consentToTreatment: v })} />
              <Check label="I confirm I have had the opportunity to ask questions and have received answers I understand." checked={acks.questionsAnswered} onChange={(v) => setAcks({ ...acks, questionsAnswered: v })} />
              <Check label="(Optional) I authorise N S Physiotherapy to submit claims and share the necessary clinical information with my medical aid where applicable." checked={acks.medicalAidClaim} onChange={(v) => setAcks({ ...acks, medicalAidClaim: v })} />
            </fieldset>

            <label className="grid gap-1.5 text-sm">
              <span className="font-semibold text-primary">Type your full name as signature <span className="text-destructive">*</span></span>
              <input required value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Your full name" className="rounded-md border border-input bg-background px-3 py-2" />
              <span className="text-xs text-muted-foreground">By typing your name and submitting, you confirm this is your electronic signature.</span>
            </label>

            {error && <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}

            <button disabled={!canSubmit} className="btn-hero inline-flex h-12 items-center justify-center rounded-full px-6 font-semibold disabled:opacity-50">
              {submitting ? "Submitting..." : "Sign & submit consent"}
            </button>
          </form>
        )}
      </section>
    </PageShell>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 rounded border-input" />
      <span className="text-foreground/85">{label}</span>
    </label>
  );
}
